import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useApi } from "../useApi";
import {
  BODY_VALUES,
  NEGATIVE_NOTES,
  POSITIVE_NOTES,
  type Rating,
  type RatingInput,
} from "../types";
import { ErrorBox } from "../components/ErrorBox";
import { StarRating } from "../components/StarRating";
import { TagPicker } from "../components/TagPicker";

const blankRating: RatingInput = {
  stars: 0,
  positive_notes: [],
  negative_notes: [],
  body: null,
  brew_method: null,
  notes: "",
};

export function RateRoast() {
  const { roastId = "" } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const [form, setForm] = useState<RatingInput>(blankRating);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [ok, setOk] = useState<string | null>(null);

  useEffect(() => {
    api
      .getRating(roastId)
      .then((r) => {
        if (r.rating) setForm(ratingToInput(r.rating));
      })
      .catch(setErr)
      .finally(() => setLoaded(true));
  }, [api, roastId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setOk(null);
    setSaving(true);
    try {
      await api.rateRoast(roastId, form);
      setOk("Saved.");
      setTimeout(() => navigate(`/roasts/${encodeURIComponent(roastId)}`), 400);
    } catch (e) {
      setErr(e);
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <p className="muted">Loading…</p>;

  return (
    <form className="col" onSubmit={submit}>
      <div>
        <Link to={`/roasts/${encodeURIComponent(roastId)}`}>← Back</Link>
      </div>
      <h1 style={{ margin: 0 }}>Rate {roastId}</h1>

      <div className="card col">
        <label className="col">
          <span className="muted small">Stars</span>
          <StarRating
            value={form.stars}
            onChange={(n) => setForm({ ...form, stars: n })}
            size={24}
          />
        </label>

        <TagPicker
          label="Positive notes"
          options={POSITIVE_NOTES}
          value={form.positive_notes}
          onChange={(v) => setForm({ ...form, positive_notes: v })}
        />
        <TagPicker
          label="Negative notes"
          options={NEGATIVE_NOTES}
          value={form.negative_notes}
          onChange={(v) => setForm({ ...form, negative_notes: v })}
        />

        <label className="col">
          <span className="muted small">Body</span>
          <select
            value={form.body ?? ""}
            onChange={(e) =>
              setForm({
                ...form,
                body: (e.target.value || null) as RatingInput["body"],
              })
            }
          >
            <option value="">—</option>
            {BODY_VALUES.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </label>

        <label className="col">
          <span className="muted small">Brew method (free text)</span>
          <input
            value={form.brew_method ?? ""}
            onChange={(e) =>
              setForm({ ...form, brew_method: e.target.value || null })
            }
            placeholder="v60, espresso, aeropress…"
          />
        </label>

        <label className="col">
          <span className="muted small">Notes</span>
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={4}
            placeholder="Free text. Never matched against — just surfaced."
          />
        </label>
      </div>

      <ErrorBox error={err} />
      {ok && <div className="ok-box">{ok}</div>}

      <div className="row">
        <button type="submit" disabled={saving}>
          {saving ? "Saving…" : "Save rating"}
        </button>
        <button
          type="button"
          className="secondary"
          onClick={() => navigate(`/roasts/${encodeURIComponent(roastId)}`)}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function ratingToInput(r: Rating): RatingInput {
  return {
    stars: r.stars,
    positive_notes: r.positive_notes ?? [],
    negative_notes: r.negative_notes ?? [],
    body: r.body,
    brew_method: r.brew_method,
    notes: r.notes ?? "",
  };
}
